import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../../components/Button';
import { useIsDark } from '../../../hooks/useTheme';
import type { DroneMarkerHandle, HavorixMap, TrailHandle } from '../../../lib/map';
import { DEFAULT_CENTER, DEFAULT_ZOOM, createBaseMap } from '../../../lib/map';
import { useTelemetryFrames } from '../../../drone/hooks';
import { getTrail, useTrailStore } from '../../../drone/trail';
import type { DroneTelemetry } from '@shared/types';
import type { FleetStatus } from '../../../lib/droneStatus';

/** 跟随视角的刷新节奏：比遥测帧率低得多，否则地图会一直重启动画 */
const FOLLOW_INTERVAL_MS = 400;
const FIT_PADDING_PX = 80;
const RECENTER_MS = 400;

interface MiniMapProps {
  droneId: string;
  name: string;
  /** store 中的兜底帧 */
  telemetry: DroneTelemetry | null;
  status: FleetStatus;
  className?: string;
}

/**
 * 单机地图。
 *
 * 位置更新走两路：航迹按 ~4 Hz 节流重建折线，飞机标记则每帧直接更新位置与朝向，
 * 因此地图既不会在 20 Hz 下抖动，也不会漏掉最新位置。
 * 组件只传 WGS84 坐标，GCJ-02 转换全部由 `lib/map.ts` 内部完成。
 */
export function MiniMap({ droneId, name, telemetry, status, className = '' }: MiniMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<HavorixMap | null>(null);
  /**
   * 逐帧回调（遥测帧率）不能依赖 React 状态，否则每次建图/卸载都要重新订阅，
   * 所以这里额外留一份 ref 给命令式调用用。
   */
  const mapRef = useRef<HavorixMap | null>(null);
  const markerRef = useRef<DroneMarkerHandle | null>(null);
  const trailRef = useRef<TrailHandle | null>(null);
  const followRef = useRef(true);
  const dark = useIsDark();
  const trailVersion = useTrailStore((state) => state.version);
  const [follow, setFollow] = useState(true);
  const lastFollowAtRef = useRef(0);

  const lastTelemetryRef = useRef<DroneTelemetry | null>(telemetry);
  lastTelemetryRef.current = telemetry;

  // 建图（换机时才重建，深浅色另行同步）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let created: HavorixMap | null = null;

    // 用户手动拖动地图时暂停自动跟随
    const pauseFollow = () => {
      followRef.current = false;
      setFollow(false);
    };

    void createBaseMap(container, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      dark: document.documentElement.classList.contains('dark'),
      lockRotation: true,
    }).then((instance) => {
      if (!instance) return;
      // 建图期间组件已卸载（或已换机）：直接销毁，避免留下孤儿实例
      if (disposed) {
        instance.destroy();
        return;
      }
      instance.onDragStart(pauseFollow);
      created = instance;
      mapRef.current = instance;
      setMap(instance);
    });

    return () => {
      disposed = true;
      created?.offDragStart(pauseFollow);
      created?.destroy();
      created = null;
      mapRef.current = null;
      markerRef.current = null;
      trailRef.current = null;
      setMap(null);
    };
  }, [droneId]);

  useEffect(() => {
    map?.setDark(dark);
  }, [map, dark]);

  // 标记与航迹图层各建一次
  useEffect(() => {
    if (!map) return;
    if (!markerRef.current) {
      markerRef.current = map.addMarker(
        {
          status,
          headingDeg: telemetry?.flight.armed ? telemetry.attitude.yawDeg : null,
          selected: true,
          label: name,
        },
        telemetry?.position.lng ?? DEFAULT_CENTER[0],
        telemetry?.position.lat ?? DEFAULT_CENTER[1],
      );
      if (telemetry) {
        map.fitBounds([[telemetry.position.lng, telemetry.position.lat] as const], FIT_PADDING_PX);
      }
    }
    if (!trailRef.current) trailRef.current = map.createTrail();
  }, [map, status, name, telemetry]);

  // 每帧：更新标记位置与朝向（不动 React）
  useTelemetryFrames(droneId, (frame) => {
    lastTelemetryRef.current = frame;
    markerRef.current?.setPosition(frame.position.lng, frame.position.lat);
    markerRef.current?.setAppearance({
      status,
      headingDeg: frame.flight.armed ? frame.attitude.yawDeg : null,
      selected: true,
      label: name,
    });
    if (followRef.current && Date.now() - lastFollowAtRef.current >= FOLLOW_INTERVAL_MS) {
      lastFollowAtRef.current = Date.now();
      mapRef.current?.centerOn(frame.position.lng, frame.position.lat, FOLLOW_INTERVAL_MS);
    }
  });

  // 兜底帧：链路断开时把标记放到最后已知位置
  useEffect(() => {
    if (!telemetry) return;
    markerRef.current?.setPosition(telemetry.position.lng, telemetry.position.lat);
  }, [telemetry]);

  // 状态/名称变化时同步外观（不依赖遥测帧）
  useEffect(() => {
    markerRef.current?.setAppearance({
      status,
      headingDeg: telemetry?.flight.armed ? telemetry.attitude.yawDeg : null,
      selected: true,
      label: name,
    });
  }, [status, name, telemetry]);

  const trail = useMemo(() => {
    void trailVersion;
    return getTrail(droneId);
  }, [droneId, trailVersion]);

  useEffect(() => {
    trailRef.current?.setPath(trail);
  }, [trail, map]);

  return (
    <div className={['relative overflow-hidden', className].join(' ')}>
      <div ref={containerRef} className="havorix-map-container size-full" />
      <div className="absolute top-2 right-2 flex flex-col gap-1.5">
        <Button
          size="sm"
          variant={follow ? 'primary' : 'default'}
          onClick={() => {
            const next = !followRef.current;
            followRef.current = next;
            setFollow(next);
            if (next) {
              const current = lastTelemetryRef.current;
              if (current) {
                mapRef.current?.centerOn(
                  current.position.lng,
                  current.position.lat,
                  RECENTER_MS,
                );
              }
            }
          }}
        >
          {follow ? '跟随中' : '已暂停跟随'}
        </Button>
        <Button
          size="sm"
          onClick={() => {
            const current = lastTelemetryRef.current;
            if (!current) return;
            mapRef.current?.centerOn(current.position.lng, current.position.lat, RECENTER_MS);
          }}
        >
          回到机体
        </Button>
      </div>
      {!telemetry ? (
        <div className="pointer-events-none absolute bottom-2 left-2 rounded border border-caution bg-caution-soft px-2 py-1 text-[11px] font-medium text-fg">
          尚未收到位置数据，地图显示默认区域
        </div>
      ) : null}
    </div>
  );
}
